import { useApi } from '../classes/appApi'
import { useOffline } from '../components/App/AppOfflineContext'
import { useRouterContext } from '../components/App/AppRouter'
import { useTenantContext } from '../components/App/AppTenant'
import MainLayout, { useMainLayoutContext } from '../components/MainLayout'
import { PayslipSummaryModel } from '../defs/defs'
import EqxCard from '../shared/components/EqxCard/EqxCard'
import EqxError from '../shared/components/EqxError'
import { EqxLink } from '@equinoxx/react'
import EqxListGroup, { EqxListGroupItem } from '../shared/components/EqxListGroup/EqxListGroup'
import EqxLoading from '../shared/components/EqxLoading'
import EqxRequestHandler from '../shared/components/EqxRequestHandler/EqxRequestHandler'
import useApiEndpoint from '../shared/hooks/ApiEndpoint'
import { formatDate } from '../shared/utils/formatDate'

// Main content component extracted from PagePayslipsIndex
const PayslipsIndexContent = () => {
    const api = useApi()
    const { response: payslips, loading, error } = useApiEndpoint<PayslipSummaryModel[]>(api, api.endpoints.payslips())
    const router = useRouterContext()
    const { isOffline, isFileCached } = useOffline()
    const tenant = useTenantContext()
    const mainContext = useMainLayoutContext()
    if (!mainContext.permissions?.canViewPayslips) {
        return <EqxError error="You do not have permission to view payslips." />
    }
    if (!tenant.isManagedPayroll) {
        return <EqxError error="Managed Payroll is not enabled for this business" />;
    }
    if (!mainContext.user?.staffID) {
        if (!mainContext.user) {
            return <EqxLoading />
        } else {
            return <EqxError error="Only staff can view payslips" />;
        }
    }

    return (
        <EqxRequestHandler
            state={{ loading, error: error ?? null }}
            isEmpty={payslips?.length === 0}
            empty={
                <EqxCard isSection>
                    <div className="p-5 text-center text-muted">No payslips found</div>
                </EqxCard>
            }
        >
            <EqxCard isSection noPadding>
                <EqxListGroup isChild>
                    {payslips?.map((payslip) => (
                        <EqxListGroupItem key={payslip.id}>
                            <div className="clickable d-flex" onClick={() => router.navigate(`/payslips/${payslip.id}`)}>
                                <div className="flex-grow-1">
                                    <h6 className="mb-0">
                                        <EqxLink
                                            to={`/payslips/${payslip.id}`}
                                            className={isOffline && !isFileCached(`/payslips/${payslip.id}`) ? 'unavailable' : ''}
                                        >
                                            {payslip.displayNameForStaff || payslip.externalRef || `Payslip #${payslip.id}`}
                                        </EqxLink>
                                    </h6>
                                    <div className="small text-muted">
                                        {payslip.payrollDate && `Payroll Date: ${formatDate(payslip.payrollDate)}`}
                                        {payslip.payrollDate && payslip.periodStartDate && ' • '}
                                        {payslip.periodStartDate && payslip.periodFinishDate &&
                                            `Period: ${formatDate(payslip.periodStartDate)} - ${formatDate(payslip.periodFinishDate)}`
                                        }
                                    </div>
                                </div>
                                <div className="ms-3 text-end">
                                    {payslip.grossAmount && (
                                        <div className="small">
                                            <div className="text-muted">Gross: &pound;{payslip.grossAmount.toFixed(2)}</div>
                                            {payslip.netAmount && (
                                                <div className="text-success">Net: &pound;{payslip.netAmount.toFixed(2)}</div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </EqxListGroupItem>
                    ))}
                </EqxListGroup>
            </EqxCard>
        </EqxRequestHandler>
    )
}

const PagePayslipsIndex = () => {
    return (
        <MainLayout pageTitle="Payslips">
            <PayslipsIndexContent />
        </MainLayout>
    )
}

export default PagePayslipsIndex 